import { useEffect, useRef } from "react";
import {
  analyticsKeyPrefix,
  type AnalyticsOwner,
} from "../../lib/analyticsOwner";
import { useHookCache } from "../../lib/hookCache";
import { useSessionRunsContext } from "../../contexts/sessionRunsContext";
import { SessionRun, SessionRunState } from "../../models/audit/sessionRun";

function isQueuedLikeState(state: SessionRunState): boolean {
  // Treat all not-yet-terminal states as "still working" so we only emit
  // an invalidation on the actual flip *into* a terminal state.
  return (
    state === "queued" ||
    state === "leased" ||
    state === "retry" ||
    state === "local_run" ||
    state === "dispatched"
  );
}

export function isTerminalState(state: SessionRunState): boolean {
  return (
    state === "succeeded" ||
    state === "timed_out" ||
    state === "failed" ||
    state === "cancelled" ||
    state === "superseded" ||
    // The degraded sentinel for a state this build doesn't know (MS-3366).
    // The grid already treats it as settled (not in ACTIVE_STATES), so it
    // must invalidate here too — otherwise the cell stops shimmering but
    // keeps showing the pre-run value.
    state === "unknown"
  );
}

/**
 * Detects queued-like → terminal state transitions (`succeeded`, `timed_out`,
 * `failed`, `cancelled`, `superseded`) on `SessionRun`s — plus ANY transition into
 * `cancelled`, since a stop retro-cancels runs that had already settled (an
 * already-`succeeded` sample swept by a stop never passes through a
 * queued-like state) — and invalidates every cache entry a finished run can
 * have changed. The sweeps are keyed per flipped row, per session, and — once
 * per batch — per control; each carries its own reason at its call site below.
 *
 * The hook runs at shell scope, alongside `useControlSessionStatus`, because
 * the shell stacks every enabled procedure session at once and a flip belongs
 * to whichever of them owns the run — no single child can observe them all.
 * Its sweeps are keyed per row and per session rather than blanket, so one
 * sample settling does not refetch the whole view.
 *
 * `controlSessionId` is accepted for parity with sibling polling hooks but is
 * only used to no-op when the workpaper view is mounted without an id and to
 * key the envelope refresh; the actual diff source is the global session-runs
 * context.
 *
 * `analyticsOwner` keys the analytics reads, which are scoped to the control or
 * work step rather than to the session (its `analyticsKeyPrefix`). It is
 * optional: when the envelope has not resolved yet there is no id to sweep
 * with, and every session-scoped invalidation still fires.
 */
export function useSessionRunsTransitions(
  controlSessionId: string | undefined,
  analyticsOwner: AnalyticsOwner | undefined,
): void {
  const analyticsOwnerKind = analyticsOwner?.kind;
  const analyticsOwnerId = analyticsOwner?.id;
  const { sessionRuns } = useSessionRunsContext();
  const { invalidate } = useHookCache();
  const previousByIdRef = useRef<Map<string, SessionRun>>(new Map());

  useEffect(() => {
    if (!controlSessionId) {
      previousByIdRef.current = new Map();
      return;
    }
    const prevMap = previousByIdRef.current;
    const nextMap = new Map<string, SessionRun>();
    let anyTerminalFlip = false;
    for (const run of sessionRuns) {
      nextMap.set(run.id, run);
      const prev = prevMap.get(run.id);
      // Ordinary completion is a queued-like → terminal flip. A stop is the
      // exception: it retro-cancels runs in ANY prior state (an
      // already-`succeeded` sample gets swept to `cancelled` with no
      // queued-like state in between), so any flip into `cancelled` counts.
      const transitionedToTerminal =
        prev &&
        ((isQueuedLikeState(prev.state) && isTerminalState(run.state)) ||
          (prev.state !== "cancelled" && run.state === "cancelled") ||
          // Same exception, for the degraded sentinel: `ready` is active to the
          // grid but is not queued-like here, so a `ready -> unknown` flip would
          // otherwise stop the shimmer without refreshing the cache and leave the
          // pre-run value on screen. Any flip into `unknown` counts (MS-3366).
          (prev.state !== "unknown" && run.state === "unknown")) &&
        run.sessionId &&
        run.sourceRowId;
      if (transitionedToTerminal) {
        void invalidate([
          "procedure-session",
          run.sessionId,
          "source-rows",
          run.sourceRowId,
        ]);
        void invalidate(["workpaper-data", run.sessionId]);
        // Workpaper-lazy-load: wipe per-cell citation caches for the row
        // whose tests just regenerated (citation lists may have changed)
        // plus the per-attribute-test detail caches for the whole session
        // (coarse but cheap — the keyed entry refetches lazily on next
        // mount of the panel).
        void invalidate([
          "procedure-session",
          run.sessionId,
          "support-cell-citations",
          run.sourceRowId,
        ]);
        void invalidate([
          "procedure-session",
          run.sessionId,
          "attribute-test-result",
        ]);
        // This sweep reaches the session-scoped verdict only. The analytics
        // status banner, run manifest and run steps are keyed under the
        // analytics owner's prefix, a different one, and are swept once per
        // batch below.
        void invalidate([
          "procedure-session",
          run.sessionId,
          "analytics-result",
        ]);
        // Surface fresh `attributeTestResultsSummary` / `checkResultsSummary`
        // on the row's cells: `AttrTestColumnCell` reads its result from the
        // table-shell, so without this invalidation the cell stays stale
        // until the next session-wide transition.
        void invalidate(["procedure-session", run.sessionId, "table-shell"]);
        anyTerminalFlip = true;
      }
    }
    // Single-row reruns don't cycle the session-level lifecycle states, so
    // the envelope refresh in `useControlSessionStatus` never fires for them —
    // yet one persisted trail row is enough to flip the envelope's
    // `auditorTrailEnabled`/`auditorTrailPending` (the BE check is an EXISTS).
    // Refresh the envelope once per batch of terminal flips so the
    // walkthrough switch and the rerun-pending banner react without a reload.
    if (anyTerminalFlip) {
      void invalidate(["control-session", controlSessionId]);
    }
    // The analytics cell's evidence — status banner, run manifest, run steps —
    // is keyed by the analytics owner, not by session, so no session-scoped
    // sweep above reaches it. Leaving it would pair the fresh verdict with the
    // superseded run's output files, which in an audit is misattributed
    // evidence. Swept once per batch because the entries are per-owner, not
    // per-row.
    if (anyTerminalFlip && analyticsOwnerKind && analyticsOwnerId) {
      void invalidate(
        analyticsKeyPrefix({ kind: analyticsOwnerKind, id: analyticsOwnerId }),
      );
    }
    previousByIdRef.current = nextMap;
  }, [
    sessionRuns,
    controlSessionId,
    analyticsOwnerKind,
    analyticsOwnerId,
    invalidate,
  ]);
}
